import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Home as HomeIcon, Heart as HeartIcon, ShoppingBag as ShoppingBagIcon, Truck as TruckIcon, ArrowLeft, Loader, Search } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { API_URL } from '../config';
import TopBar from '../components/TopBar';
import Header from '../components/Header';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import './SearchPage.css';



const filterProductsBySearch = (products, query) => {
  const normQuery = query.toLowerCase().trim();
  if (!normQuery) return [];
  
  return products.filter(p => 
    p.title.toLowerCase().includes(normQuery) || 
    p.type.toLowerCase().includes(normQuery) ||
    (p.badge && p.badge.toLowerCase().includes(normQuery))
  );
};

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const navigate = useNavigate();
  const { wishlistCount, cartCount } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [query]);

  useEffect(() => {
    if (!query) {
      setProducts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    fetch(`${API_URL}/products`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load products');
        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data)) {
          const filtered = filterProductsBySearch(data, query);
          setProducts(filtered);
        } else {
          throw new Error('Invalid products format');
        }
      })
      .catch((err) => {
        console.error('Error fetching search products:', err);
        setError('Error performing search. Please try again.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [query]);

  return (
    <div className="search-page-wrapper">
      {/* Top Announcement Bar */}
      <TopBar />

      {/* Main Header */}
      <Header 
        wishlistCount={wishlistCount} 
        cartCount={cartCount} 
        onCartClick={() => navigate('/cart')} 
      />

      {/* Navigation Bar */}
      <Navbar activeItem="" />

      {/* Main Content */}
      <main className="search-main">
        <div className="container">
          <div className="search-header-row">
            <Link to="/" className="back-btn">
              <ArrowLeft size={16} />
              <span>Back to Shop</span>
            </Link>
            <h1 className="search-page-title">
              Search Results for <span className="query-highlight">"{query}"</span> ({products.length})
            </h1>
          </div>

          {loading ? (
            <div className="search-loader-container">
              <Loader className="spinner" size={40} />
              <p>Searching database...</p>
            </div>
          ) : error ? (
            <div className="search-error-state">
              <p>{error}</p>
            </div>
          ) : products.length > 0 ? (
            <div className="search-products-grid">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="search-empty-state">
              <div className="empty-search-icon-wrapper">
                <Search size={40} color="var(--primary-green)" />
              </div>
              <h2>No Results Found</h2>
              <p>We couldn't find any products matching "{query}". Try checking your spelling or search for common items like nuts, almonds, or makhanas.</p>
              <Link to="/" className="btn-return-home">
                Go to Homepage
              </Link>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <Footer />

      {/* Sticky Bottom Navigation (Mobile screens only) */}
      <div className="mobile-sticky-nav" role="navigation" aria-label="Mobile Navigation">
        <Link 
          to="/" 
          className="mobile-nav-item"
        >
          <HomeIcon size={20} />
          <span>Home</span>
        </Link>
        <Link 
          to="/track" 
          className="mobile-nav-item"
        >
          <TruckIcon size={20} />
          <span>Track Order</span>
        </Link>
        <Link 
          to="/wishlist" 
          className="mobile-nav-item"
        >
          <HeartIcon size={20} />
          {wishlistCount > 0 && (
            <span className="badge-count">{wishlistCount}</span>
          )}
          <span>Wishlist</span>
        </Link>
        <Link 
          to="/cart" 
          className="mobile-nav-item"
        >
          <ShoppingBagIcon size={20} />
          {cartCount > 0 && (
            <span className="badge-count">{cartCount}</span>
          )}
          <span>Cart</span>
        </Link>
      </div>
    </div>
  );
};

export default SearchPage;
